import api from "./api";

/**
 * Fire a funnel event. Silent on failure — never blocks the user flow.
 *
 * Known event names (matched server-side):
 *   • "site_visit"       — page view (fired once per session per path)
 *   • "cta_click"        — click on "Entra nel Vault"
 *   • "register_start"   — user landed on /register form
 *   • "register_complete"— registration succeeded
 */
export function trackFunnel(event, extra = {}) {
  try {
    const payload = {
      event,
      path:
        typeof window !== "undefined" ? window.location.pathname : "",
      referrer:
        typeof document !== "undefined" ? document.referrer || "" : "",
      ...extra,
    };
    // Don't await — fire and forget. Errors are swallowed intentionally
    // so a failing analytics endpoint never disrupts the UX.
    api.post("/analytics/event", payload).catch(() => {});
  } catch {
    /* no-op */
  }
}

/**
 * Track the first page visit of the browsing session. Dedupe with
 * sessionStorage so refreshing doesn't inflate the counter.
 */
export function trackSessionVisit() {
  try {
    if (typeof window === "undefined") return;
    const KEY = "gd_visit_tracked";
    if (window.sessionStorage.getItem(KEY)) return;
    window.sessionStorage.setItem(KEY, "1");
    trackFunnel("site_visit");
  } catch {
    /* no-op */
  }
}

// ---------------------------------------------------------------------------
// Google Ads conversion tracking
// ---------------------------------------------------------------------------

// Conversion label registered in Google Ads for garagedrops.com (Lead form).
const GADS_LEAD_CONVERSION_ID = "AW-18301767709/wpudCJ_utMscEJ2g-5ZE";

/**
 * SHA-256 hash the given string using the browser's SubtleCrypto.
 * Used for Google Ads Enhanced Conversions (email/phone hashing must
 * happen client-side, so the raw address never leaves the browser).
 */
async function _sha256Hex(input) {
  if (!input || typeof crypto === "undefined" || !crypto.subtle) return null;
  try {
    const enc = new TextEncoder().encode(String(input).trim().toLowerCase());
    const buf = await crypto.subtle.digest("SHA-256", enc);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

/**
 * Report a lead conversion to Google Ads (e.g. member signup, contact form).
 *
 * The gtag function is defined in index.html (Consent Mode v2 initialiser),
 * so calling it here is always safe: if the user hasn't consented to
 * marketing cookies, Consent Mode simply suppresses the outgoing pixel.
 *
 * `kind`   — free-form label for granular reporting ("signup", "contact-form")
 * `userData` — optional { email, phone, firstName, lastName } for Enhanced
 *              Conversions. The email/phone are SHA-256 hashed in the browser
 *              before being sent — raw values never leave the device.
 */
export async function trackLeadConversion(kind, userData) {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") {
      return;
    }
    // Enhanced Conversions: hash user-provided data client-side and pass it
    // to gtag via the `set` command before the conversion event. Google
    // matches the hash server-side to attribute the conversion.
    if (userData && typeof userData === "object") {
      const emailHash = userData.email
        ? await _sha256Hex(userData.email)
        : null;
      const phoneHash = userData.phone
        ? await _sha256Hex(userData.phone)
        : null;
      const enhanced = {};
      if (emailHash) enhanced.sha256_email_address = emailHash;
      if (phoneHash) enhanced.sha256_phone_number = phoneHash;
      if (Object.keys(enhanced).length) {
        window.gtag("set", "user_data", enhanced);
      }
    }
    window.gtag("event", "conversion", {
      send_to: GADS_LEAD_CONVERSION_ID,
      value: 1.0,
      currency: "EUR",
      event_label: kind || "lead",
    });
  } catch {
    /* no-op */
  }
}
